import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, FileText, Receipt, Building2, MessageSquareWarning, AlertCircle, Calendar, Video, Banknote, Inbox, BookOpen, Bell, UserCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { formatDate, formatCurrencyAmount } from '../../lib/format';
import { StatCard, Spinner, EmptyState, StatusBadge, Alert } from '../../components/ui';
import QuickLinks from '../../components/QuickLinks';
import DashboardStatus from '../../components/DashboardStatus';
import { useLivePoll } from '../../lib/useLivePoll';
import { isStaffProfileComplete } from '../../components/Profile';

const QUICK_LINKS = [
  { to: '/staff/assignments', label: 'Assignments', icon: <ClipboardList className="h-5 w-5" />, description: 'My tasks' },
  { to: '/staff/reports', label: 'Daily Report', icon: <FileText className="h-5 w-5" />, description: 'Submit & history' },
  { to: '/staff/expenses', label: 'Expenses', icon: <Receipt className="h-5 w-5" />, description: 'My claims' },
  { to: '/staff/bills', label: 'Bills', icon: <Banknote className="h-5 w-5" />, description: 'Site bills' },
  { to: '/staff/complaints', label: 'Complaints', icon: <MessageSquareWarning className="h-5 w-5" />, description: 'Resident issues' },
  { to: '/staff/meetings', label: 'Meetings', icon: <Calendar className="h-5 w-5" />, description: 'Schedule & join' },
  { to: '/staff/requests', label: 'Request', icon: <Inbox className="h-5 w-5" />, description: 'My requests' },
  { to: '/staff/handbook', label: 'Staff Handbook', icon: <BookOpen className="h-5 w-5" />, description: 'Policies & docs' },
  { to: '/staff/notices', label: 'Notices', icon: <Bell className="h-5 w-5" />, description: 'Announcements' },
  { to: '/staff/profile', label: 'My Profile', icon: <UserCircle className="h-5 w-5" />, description: 'Salary & next of kin' },
];

interface StaffDashboardData {
  stats: {
    totalAssignments: number;
    pendingAssignments: number;
    inProgressAssignments: number;
    completedAssignments: number;
    myReports: number;
    myExpenses: number;
    expenseAmount: number;
    availableProperties: number;
    myComplaints: number;
    openComplaints: number;
  };
  myAssignments: Array<{ id: number; title: string; due_date: string; status: string; progress: number }>;
  recentComplaints: Array<{ id: number; subject: string; status: string; created_at: string }>;
  upcomingMeetings: Array<{ id: number; title: string; meeting_date: string; duration: number; meeting_url: string; status: string }>;
}

export default function Dashboard() {
  const { data, loading, refreshing, lastUpdated, reload } = useLivePoll<StaffDashboardData>(
    () => api.get<StaffDashboardData>('/dashboard')
  );
  const [profileComplete, setProfileComplete] = useState(true);

  useEffect(() => {
    api
      .get<{ profile: { bank_name: string | null; bank_account: string | null; account_name: string | null; next_of_kin_name: string | null; next_of_kin_phone: string | null } | null }>('/auth/profile')
      .then((d) => setProfileComplete(isStaffProfileComplete(d.profile)))
      .catch(() => {});
  }, []);

  if (loading) return <Spinner label="Loading dashboard..." />;
  if (!data) return <EmptyState message="Could not load dashboard data." />;

  const s = data.stats;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <DashboardStatus lastUpdated={lastUpdated} refreshing={refreshing} onRefresh={reload} />
      </div>

      {!profileComplete && (
        <div className="mb-6">
          <Alert>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <span className="inline-flex items-center"><AlertCircle className="h-5 w-5 mr-2 shrink-0" /> Please complete your salary and next of kin details.</span>
              <Link to="/staff/profile" className="text-primary text-sm hover:underline whitespace-nowrap">Complete now</Link>
            </div>
          </Alert>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={<ClipboardList className="h-8 w-8" />}
          label="My Assignments"
          value={s.totalAssignments}
          sub={`${s.pendingAssignments} pending · ${s.inProgressAssignments} in progress · ${s.completedAssignments} completed`}
        />
        <StatCard
          icon={<FileText className="h-8 w-8" />}
          label="Reports Submitted"
          value={s.myReports}
          color="text-purple-600"
        />
        <StatCard
          icon={<Receipt className="h-8 w-8" />}
          label="My Expenses"
          value={s.myExpenses}
          color="text-yellow-600"
          sub={formatCurrencyAmount(s.expenseAmount, 'NGN')}
        />
        <StatCard
          icon={<Building2 className="h-8 w-8" />}
          label="Available Properties"
          value={s.availableProperties}
          color="text-green-600"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={<MessageSquareWarning className="h-8 w-8" />}
          label="My Complaints"
          value={s.myComplaints}
          color="text-red-600"
        />
        <StatCard
          icon={<MessageSquareWarning className="h-8 w-8" />}
          label="Open Complaints"
          value={s.openComplaints}
          color="text-yellow-600"
          sub="awaiting response"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <h2 className="text-xl font-semibold">My Assignments</h2>
            <Link to="/staff/assignments" className="text-primary text-sm hover:underline">View all</Link>
          </div>
          {data.myAssignments.length === 0 ? (
            <EmptyState message="No assignments yet." />
          ) : (
            <div className="space-y-4">
              {data.myAssignments.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 flex-wrap py-2 border-b last:border-b-0">
                  <div>
                    <p className="font-medium">{a.title}</p>
                    <p className="text-sm text-gray-500">Due: {formatDate(a.due_date)}</p>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <h2 className="text-xl font-semibold">My Recent Complaints</h2>
            <Link to="/staff/complaints" className="text-primary text-sm hover:underline">View all</Link>
          </div>
          {data.recentComplaints.length === 0 ? (
            <EmptyState message="No complaints yet." />
          ) : (
            <div className="space-y-4">
              {data.recentComplaints.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 flex-wrap py-2 border-b last:border-b-0">
                  <div>
                    <p className="font-medium">{c.subject}</p>
                    <p className="text-sm text-gray-500">{formatDate(c.created_at)}</p>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-xl font-semibold mb-4">Upcoming Meetings</h2>
        {data.upcomingMeetings.length === 0 ? (
          <EmptyState message="No upcoming meetings." />
        ) : (
          <div className="space-y-4">
            {data.upcomingMeetings.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 flex-wrap py-2 border-b last:border-b-0">
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 text-primary mr-3 shrink-0" />
                  <div>
                    <p className="font-medium">{m.title}</p>
                    <p className="text-sm text-gray-500">{formatDate(m.meeting_date)} · {m.duration} min</p>
                  </div>
                </div>
                <a href={m.meeting_url} target="_blank" rel="noreferrer" className="text-primary text-sm hover:underline inline-flex items-center">
                  <Video className="h-4 w-4 mr-1" /> Join
                </a>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8">
        <QuickLinks links={QUICK_LINKS} title="All Information" />
      </div>
    </div>
  );
}